import { supabase } from "./churchSupabaseClient";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeApiConfig(row = {}) {
  return {
    id: row.id || "",
    apiKey: row.api_key || row.apiKey || "",
    displayName: row.display_name || row.displayName || "",
    provider: row.provider || "",
    category: row.category || "",
    baseUrl: row.base_url || row.baseUrl || "",
    secretName: row.secret_name || row.secretName || "",
    publicConfig: row.public_config || row.publicConfig || {},
    isEnabled: row.is_enabled ?? row.isEnabled ?? true,
    notes: row.notes || "",
    updatedAt: row.updated_at || row.updatedAt || "",
  };
}

function normalizeDomain(row = {}) {
  return {
    id: row.id || "",
    accountId: row.account_id || row.accountId || "",
    organizationName: row.organizationName || row.organization_name || "Unassigned",
    domainName: row.domain_name || row.domainName || "",
    domainType: row.domain_type || row.domainType || "full_domain",
    status: row.status || "pending",
    source: row.domainSource || row.domain_source || row.source || "platform",
    isPrimary: row.is_primary === true || row.isPrimary === true,
    vercelProjectId: row.vercel_project_id || row.vercelProjectId || "",
    vercelDomainId: row.vercel_domain_id || row.vercelDomainId || "",
    verificationData: row.verification_data || row.verificationData || {},
    notes: row.notes || "",
    createdAt: row.created_at || row.createdAt || "",
  };
}

function normalizeDomainRequest(row = {}) {
  return {
    id: row.id || "",
    accountId: row.account_id || row.accountId || "",
    organizationName: row.organizationName || row.organization_name || "Unassigned",
    domainName: row.domain_name || row.domainName || "",
    requestType: row.request_type || row.requestType || "full_domain",
    status: row.status || "pending",
    requesterEmail: row.requester_email || row.requesterEmail || "",
    metadata: row.metadata || {},
    notes: row.notes || "",
    createdAt: row.created_at || row.createdAt || "",
    reviewedAt: row.reviewed_at || row.reviewedAt || "",
  };
}

function normalizeSignupRequest(row = {}) {
  return {
    id: row.id || "",
    churchName: row.church_name || row.churchName || "",
    contactName: row.contact_name || row.contactName || "",
    email: row.email || "",
    phone: row.phone || "",
    city: row.city || "",
    stateRegion: row.state_region || row.stateRegion || "",
    notes: row.notes || "",
    adminNotes: row.admin_notes || row.adminNotes || "",
    status: row.status || "pending",
    onboardingToken: row.onboarding_token || row.onboardingToken || "",
    onboardingUrl: row.onboarding_url || row.onboardingUrl || "",
    createdAt: row.created_at || row.createdAt || "",
    reviewedAt: row.reviewed_at || row.reviewedAt || "",
  };
}

function normalizeFeedbackReport(row = {}) {
  return {
    id: row.id || "",
    accountId: row.account_id || row.accountId || "",
    organizationName: row.organizationName || row.organization_name || "Unknown church",
    reporterName: row.reporter_name || row.reporterName || "",
    reporterEmail: row.reporter_email || row.reporterEmail || "",
    reportType: row.report_type || row.reportType || "error",
    title: row.title || "",
    message: row.message || "",
    pageUrl: row.page_url || row.pageUrl || "",
    status: row.status || "new",
    adminNotes: row.admin_notes || row.adminNotes || "",
    metadata: row.metadata || {},
    createdAt: row.created_at || row.createdAt || "",
    reviewedAt: row.reviewed_at || row.reviewedAt || "",
  };
}

function normalizeTileApp(row = {}) {
  return {
    tileId: row.tile_id || row.tileId || "",
    appName: row.app_name || row.appName || "",
    category: row.category || "",
    shortDescription: row.short_description || row.shortDescription || "",
    fullDescription: row.full_description || row.fullDescription || "",
    developerName: row.developer_name || row.developerName || "Oikos",
    version: row.version || "",
    iconUrl: row.icon_url || row.iconUrl || "",
    logoUrl: row.logo_url || row.logoUrl || "",
    isGloballyEnabled: row.is_globally_enabled ?? row.isGloballyEnabled ?? true,
    updatedAt: row.updated_at || row.updatedAt || "",
  };
}

function getTileAppUploadFallback(tileId = "") {
  const defaults = {
    "church-stream": {
      app_name: "Live Stream Studio",
      category: "Church",
      short_description: "Schedule YouTube lives and stream from the browser through Oikos Relay Server.",
      full_description: "Live Stream Studio lets church teams connect YouTube, schedule broadcasts, capture camera, microphone, and display sources, and stream through the Oikos Relay Server.",
      developer_name: "Oikos Software",
      version: "1.0.0",
      is_globally_enabled: true,
    },
  };

  return {
    tile_id: tileId,
    app_name: tileId,
    category: "Church",
    short_description: "",
    full_description: "",
    developer_name: "Oikos Software",
    version: "1.0.0",
    is_globally_enabled: true,
    ...(defaults[tileId] || {}),
  };
}

function normalizeGlobalStyles(row = {}) {
  return {
    id: row.id || "platform",
    interactiveBackgroundUrl: row.interactive_background_url || row.interactiveBackgroundUrl || "",
    interactiveBackgroundPath: row.interactive_background_path || row.interactiveBackgroundPath || "",
    loginBackgroundUrl: row.login_background_url || row.loginBackgroundUrl || "",
    loginBackgroundPath: row.login_background_path || row.loginBackgroundPath || "",
    updatedAt: row.updated_at || row.updatedAt || "",
  };
}

function isMissingGlobalStylesTable(error) {
  const message = String(error?.message || "").toLowerCase();
  return error?.code === "42P01" || message.includes("global_admin_styles");
}

function isMissingSignupRequests(error) {
  const message = String(error?.message || "").toLowerCase();
  return (
    error?.code === "42P01" ||
    error?.code === "42883" ||
    message.includes("platform_signup_requests") ||
    message.includes("global_admin_list_signup_requests") ||
    message.includes("sales_signup_request")
  );
}

function isMissingFeedbackReports(error) {
  const message = String(error?.message || "").toLowerCase();
  return (
    error?.code === "42P01" ||
    error?.code === "42883" ||
    message.includes("platform_feedback_reports") ||
    message.includes("global_admin_list_feedback_reports") ||
    message.includes("church_submit_feedback_report")
  );
}

export async function loadGlobalAdminOverview() {
  const { data, error } = await supabase.rpc("global_admin_overview");
  if (error) throw error;
  return data || {};
}

export async function loadGlobalAdminOrganizations() {
  const { data, error } = await supabase.rpc("global_admin_list_organizations");
  if (error) throw error;
  return asArray(data).map((org) => ({
    id: org.id || "",
    name: org.name || "Unnamed organization",
    type: org.type || "church",
    ownerUserId: org.ownerUserId || "",
    inviteCode: org.inviteCode || "",
    churchCode: org.churchCode || "",
    logoUrl: org.logoUrl || "",
    city: org.city || "",
    stateRegion: org.stateRegion || "",
    country: org.country || "",
    platformAccessEnabled: org.platformAccessEnabled !== false,
    createdAt: org.createdAt || "",
    localUsers: asArray(org.localUsers),
    liveDisplayCount: Number(org.liveDisplayCount || 0),
    liveScreenCount: Number(org.liveScreenCount || 0),
    domainCount: Number(org.domainCount || 0),
  }));
}

export async function setGlobalOrganizationAccess(accountId, enabled) {
  const { data, error } = await supabase.rpc("global_admin_set_organization_access", {
    p_account_id: accountId,
    p_enabled: enabled,
  });
  if (error) throw error;
  return data || {};
}

export async function loadGlobalApiConfigs() {
  const { data, error } = await supabase.rpc("global_admin_list_api_configs");
  if (error) throw error;
  return asArray(data).map(normalizeApiConfig);
}

export async function saveGlobalApiConfig(config) {
  const { data, error } = await supabase.rpc("global_admin_save_api_config", {
    p_api_key: config.apiKey,
    p_display_name: config.displayName,
    p_provider: config.provider,
    p_category: config.category,
    p_base_url: config.baseUrl,
    p_secret_name: config.secretName,
    p_public_config: config.publicConfig || {},
    p_is_enabled: config.isEnabled !== false,
    p_notes: config.notes,
  });
  if (error) throw error;
  return normalizeApiConfig(data || {});
}

export async function checkAiGatewayStatus() {
  const response = await fetch("/api/ai-gateway-status", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await response.json().catch(() => ({})) : {};

  if (!response.ok) {
    throw new Error(data?.error || data?.message || "AI Gateway status could not be checked.");
  }

  if (!isJson || (typeof data?.ok === "undefined" && typeof data?.configured === "undefined")) {
    return {
      ok: false,
      configured: false,
      message: "AI Gateway status endpoint is not running here. Deploy to Vercel or run with Vercel dev so /api/ai-gateway-status can read AI_GATEWAY_API_KEY.",
    };
  }

  return {
    ...data,
    message: data.message || (data.ok ? "AI Gateway is active." : "AI Gateway is not active yet."),
  };
}

export async function loadGlobalDomains() {
  const { data, error } = await supabase.rpc("global_admin_list_domains");
  if (error) throw error;
  return {
    domains: asArray(data?.domains).map(normalizeDomain),
    requests: asArray(data?.requests).map(normalizeDomainRequest),
  };
}

export async function submitSalesSignupRequest(request) {
  const { data, error } = await supabase.rpc("sales_signup_request", {
    p_church_name: request.churchName || "",
    p_contact_name: request.contactName || "",
    p_email: request.email || "",
    p_phone: request.phone || "",
    p_city: request.city || "",
    p_state_region: request.stateRegion || "",
    p_notes: request.notes || "",
  });
  if (error) {
    if (isMissingSignupRequests(error)) {
      throw new Error("Sales signup requests are not installed yet. Run sql/add-sales-signup-requests.sql in Supabase, then try again.");
    }
    throw error;
  }
  return data || {};
}

export async function loadGlobalSignupRequests() {
  const { data, error } = await supabase.rpc("global_admin_list_signup_requests");
  if (error) {
    if (isMissingSignupRequests(error)) return [];
    throw error;
  }
  return asArray(data).map(normalizeSignupRequest);
}

export async function updateGlobalSignupRequest(requestId, status, adminNotes = "") {
  const { data, error } = await supabase.rpc("global_admin_update_signup_request", {
    p_request_id: requestId,
    p_status: status,
    p_admin_notes: adminNotes,
  });
  if (error) throw error;
  return normalizeSignupRequest(data || {});
}

export async function loadGlobalFeedbackReports() {
  const { data, error } = await supabase.rpc("global_admin_list_feedback_reports");
  if (error) {
    if (isMissingFeedbackReports(error)) return [];
    throw error;
  }
  return asArray(data).map(normalizeFeedbackReport);
}

export async function updateGlobalFeedbackReport(reportId, status, adminNotes = "") {
  const { data, error } = await supabase.rpc("global_admin_update_feedback_report", {
    p_report_id: reportId,
    p_status: status,
    p_admin_notes: adminNotes,
  });
  if (error) throw error;
  return normalizeFeedbackReport(data || {});
}

export async function loadGlobalTileApps() {
  const { data, error } = await supabase
    .from("tile_store_apps")
    .select("*")
    .order("app_name", { ascending: true });

  if (error) throw error;
  return asArray(data).map(normalizeTileApp);
}

export async function uploadGlobalTileAppIcon(tileId, file) {
  if (!tileId) throw new Error("Choose an app before uploading an icon.");
  if (!file) throw new Error("Choose an icon file to upload.");

  const extension = file.name?.split(".").pop()?.toLowerCase() || "png";
  const safeTileId = String(tileId).replace(/[^a-z0-9-_]/gi, "-").toLowerCase();
  const filePath = `icons/${safeTileId}-${Date.now()}.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from("tile-store-screenshots")
    .upload(filePath, file, {
      cacheControl: "3600",
      contentType: file.type || "image/png",
      upsert: true,
    });

  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from("tile-store-screenshots").getPublicUrl(filePath);

  const { data, error } = await supabase
    .from("tile_store_apps")
    .update({ icon_url: publicUrl || "", updated_at: new Date().toISOString() })
    .eq("tile_id", tileId)
    .select("*")
    .single();

  if (error?.code === "PGRST116") {
    const { data: insertedData, error: insertError } = await supabase
      .from("tile_store_apps")
      .upsert(
        {
          ...getTileAppUploadFallback(tileId),
          icon_url: publicUrl || "",
          updated_at: new Date().toISOString(),
        },
        { onConflict: "tile_id" }
      )
      .select("*")
      .single();

    if (insertError) throw insertError;
    return normalizeTileApp(insertedData || {});
  }

  if (error) throw error;
  return normalizeTileApp(data || {});
}

export async function uploadGlobalTileAppLogo(tileId, file) {
  if (!tileId) throw new Error("Choose an app before uploading a logo.");
  if (!file) throw new Error("Choose a logo file to upload.");

  const extension = file.name?.split(".").pop()?.toLowerCase() || "png";
  const safeTileId = String(tileId).replace(/[^a-z0-9-_]/gi, "-").toLowerCase();
  const filePath = `logos/${safeTileId}-${Date.now()}.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from("tile-store-screenshots")
    .upload(filePath, file, {
      cacheControl: "3600",
      contentType: file.type || "image/png",
      upsert: true,
    });

  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from("tile-store-screenshots").getPublicUrl(filePath);

  const payload = { logo_url: publicUrl || "", updated_at: new Date().toISOString() };
  const { data, error } = await supabase
    .from("tile_store_apps")
    .update(payload)
    .eq("tile_id", tileId)
    .select("*")
    .single();

  if (error?.code === "PGRST116") {
    const { data: insertedData, error: insertError } = await supabase
      .from("tile_store_apps")
      .upsert(
        {
          ...getTileAppUploadFallback(tileId),
          ...payload,
        },
        { onConflict: "tile_id" }
      )
      .select("*")
      .single();

    if (insertError) throw insertError;
    return normalizeTileApp(insertedData || {});
  }

  if (error) {
    if (String(error.message || "").includes("logo_url")) {
      throw new Error("The tile_store_apps.logo_url column is missing. Run supabase/add-tile-store-app-logo-url.sql, then try uploading the logo again.");
    }
    throw error;
  }

  return normalizeTileApp(data || {});
}

export async function loadGlobalStyles() {
  const { data, error } = await supabase
    .from("global_admin_styles")
    .select("*")
    .eq("id", "platform")
    .maybeSingle();

  if (error) {
    if (isMissingGlobalStylesTable(error)) return normalizeGlobalStyles();
    throw error;
  }

  return normalizeGlobalStyles(data || {});
}

export async function uploadGlobalInteractiveBackground(file) {
  if (!file) throw new Error("Choose a background image to upload.");

  const extension = file.name?.split(".").pop()?.toLowerCase() || "png";
  const safeExtension = extension.replace(/[^a-z0-9]/gi, "") || "png";
  const filePath = `global-styles/interactive-background-${Date.now()}.${safeExtension}`;

  const { error: uploadError } = await supabase.storage
    .from("tile-store-screenshots")
    .upload(filePath, file, {
      cacheControl: "3600",
      contentType: file.type || "image/png",
      upsert: true,
    });

  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from("tile-store-screenshots").getPublicUrl(filePath);

  const { data, error } = await supabase
    .from("global_admin_styles")
    .upsert(
      {
        id: "platform",
        interactive_background_url: publicUrl || "",
        interactive_background_path: filePath,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "id" }
    )
    .select("*")
    .single();

  if (error) {
    if (isMissingGlobalStylesTable(error)) {
      throw new Error("Global styles table is not installed yet. Run sql/add-global-admin-styles.sql in Supabase, then upload again.");
    }
    throw error;
  }

  return normalizeGlobalStyles(data || {});
}

export async function uploadGlobalLoginBackground(file) {
  if (!file) throw new Error("Choose a login background image to upload.");

  const extension = file.name?.split(".").pop()?.toLowerCase() || "png";
  const safeExtension = extension.replace(/[^a-z0-9]/gi, "") || "png";
  const filePath = `global-styles/login-background-${Date.now()}.${safeExtension}`;

  const { error: uploadError } = await supabase.storage
    .from("tile-store-screenshots")
    .upload(filePath, file, {
      cacheControl: "3600",
      contentType: file.type || "image/png",
      upsert: true,
    });

  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from("tile-store-screenshots").getPublicUrl(filePath);

  const { data, error } = await supabase
    .from("global_admin_styles")
    .upsert(
      {
        id: "platform",
        login_background_url: publicUrl || "",
        login_background_path: filePath,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "id" }
    )
    .select("*")
    .single();

  if (error) {
    if (isMissingGlobalStylesTable(error) || String(error?.message || "").includes("login_background")) {
      throw new Error("Login background styles are not installed yet. Run sql/add-global-admin-styles.sql in Supabase, then upload again.");
    }
    throw error;
  }

  return normalizeGlobalStyles(data || {});
}

export async function saveGlobalDomain(domain) {
  const { data, error } = await supabase.rpc("global_admin_save_domain", {
    p_domain_id: domain.id || null,
    p_account_id: domain.accountId || null,
    p_domain_name: domain.domainName,
    p_domain_type: domain.domainType || "full_domain",
    p_status: domain.status || "pending",
    p_vercel_project_id: domain.vercelProjectId || "",
    p_vercel_domain_id: domain.vercelDomainId || "",
    p_verification_data: domain.verificationData || {},
    p_notes: domain.notes || "",
  });
  if (error) throw error;
  return normalizeDomain(data || {});
}

export async function updateGlobalDomainRequest(requestId, status, notes = "") {
  const { data, error } = await supabase.rpc("global_admin_update_domain_request", {
    p_request_id: requestId,
    p_status: status,
    p_notes: notes,
  });
  if (error) throw error;
  return normalizeDomainRequest(data || {});
}
